import React from 'react';
import { useOnCopy } from '@gamiphy/utils/react';
import { FreeShippingStore } from '@gamiphy/loyalty-station-desktop/store/freeShipping';
import { PurchaseStore } from '@gamiphy/loyalty-station-desktop/store/purchase';
import { DiscountStore } from '@gamiphy/loyalty-station-desktop/store/reward/discount';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { getDiffInMinutes } from '@gamiphy/utils/date';
import { FREESHIPPING_CODE_ACTIVE_AFTER } from '@gamiphy/loyalty-station-desktop/components/levels/constants';
import { Grid, Typography } from '@mui/material';
import { Check, ContentCopy, Info } from '@mui/icons-material';
import { DisplayStringTrans, Trans, useTranslation } from '@gamiphy/react-i18n';
import { FreeShippingCodesStatuses } from '@gamiphy/bl/freeShipping';
import { compact } from '@gamiphy/utils/array';
import { formatNumber } from '@gamiphy/utils/number';
import { Tier } from '@gamiphy/bl/level';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';

const useShippingStatusTranslation = (): Record<FreeShippingCodesStatuses, string> => {
  const { t } = useTranslation();
  return React.useMemo(() => ({
    valid: t('level.freeShipping.status.valid', 'Valid'),
    invalid: t('level.freeShipping.status.invalid', 'Invalid'),
    used: t('level.freeShipping.status.used', 'Used'),
    expired: t('level.freeShipping.status.expired', 'Expired'),
  }), []);
};

const CustomTooltip = styled(({
  className,
  ...props
}: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    position: 'relative',
    right: 100,
    width: 200,
    fontSize: 12,
    background: '#fff',
    color: '#00000099',
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  },
});

export const useTierBenefits = ({
  tier,
  openUnavowedPopup,
}: {
  tier: Tier,
  openUnavowedPopup: () => void
}): {
  content: React.ReactNode,
  subContent?: React.ReactNode
}[] => {
  const { t } = useTranslation();

  const statusTranslations = useShippingStatusTranslation();
  const {
    copied,
    onCopy,
  } = useOnCopy(FreeShippingStore?.activeCode?.code);
  const cashbackCash = React.useMemo(() => PurchaseStore.getTierCash(tier?.order ?? 0) * 100, [tier]);
  const cashbackPoints = React.useMemo(() => cashbackCash / (DiscountStore.appDiscount?.value ?? 1), [cashbackCash, DiscountStore.appDiscount?.value]);
  const {
    currentUserShipping,
    appFreeShipping,
    userFreeShipping,
  } = FreeShippingStore;
  const levelFreeShipping = React.useMemo(() => appFreeShipping?.find((item) => item.tier === tier?.id), [tier, appFreeShipping]);
  const tierAppCashback = React.useMemo(
    () => AppCashbackStore.appCashbacks.find((appCashback) => appCashback.tier === tier?.order),
    [AppCashbackStore.appCashbacks, tier],
  );
  const freeShippingDescription = React.useMemo(
    () => t('level.freeShipping.description', {
      defaultValue:
        'Users can add {{numberOfDiscounts}} free shipping coupon with a maximum value of {{maximumShippingAmount, currency}} and can be applied to orders with a minimum amount of {{minimumOrderValue, currency}}',
      numberOfDiscounts: levelFreeShipping?.numberOfDiscounts,
      minimumOrderValue: levelFreeShipping?.minimumOrderValue,
      maximumShippingAmount: levelFreeShipping?.maximumShippingAmount,
    }),
    [levelFreeShipping],
  );
  const showCashbackBenefit = React.useMemo(() => tierAppCashback?.enabled && !!LevelStore.tierConfig?.enabled && AppStore.isCashbackProgram, [
    tierAppCashback,
    LevelStore.tierConfig,
    AppStore.isCashbackProgram,
  ]);

  const generateCode = React.useCallback(async () => {
    if (UserStore.isRealUser) {
      await FreeShippingStore.generateCode();
    } else {
      openUnavowedPopup();
    }
  }, [UserStore.isRealUser]);

  const codes = React.useMemo(() => {
    if (currentUserShipping?.tier === tier?.id && LevelStore.currentTier?.id === tier?.id && FreeShippingStore.canGenerateCode()) {
      if (FreeShippingStore.activeCode) {
        let generatedCodeTooltip = '';
        if (getDiffInMinutes(new Date(), new Date(FreeShippingStore.activeCode.createdAt)) < FREESHIPPING_CODE_ACTIVE_AFTER) {
          generatedCodeTooltip = t('level.freeShipping.theCodeWillBeValid', {
            time: new Date(new Date(FreeShippingStore.activeCode.createdAt).getTime() + FREESHIPPING_CODE_ACTIVE_AFTER * 60000).toLocaleTimeString(),
          });
        }
        return (
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            onClick={onCopy}
            sx={{ cursor: 'pointer' }}
            title={generatedCodeTooltip}
          >
            {FreeShippingStore.activeCode?.code}
            <Typography sx={{ ml: 0.5 }} component="span">
              {copied ? (<Check />) : (<ContentCopy />)}
            </Typography>
          </Grid>
        );
      }
      return (
        <Grid
          onClick={generateCode}
          sx={{
            textDecoration: 'underline',
            color: '#0000ff',
            cursor: 'pointer',
          }}
        >
          <Trans
            i18nKey="level.freeShipping.generate"
            defaults="Generate Code"
          />
        </Grid>
      );
    }

    const tierAppFreeShipping = userFreeShipping?.find((app) => app.tier === tier?.id);

    const tierCode = FreeShippingStore?.lastNotValidCode(tierAppFreeShipping?.id ?? '');
    if (tierCode) {
      const codeStatus = statusTranslations[tierCode.status];
      return (
        <Grid container alignItems="center" justifyContent="center" onClick={onCopy}>
          {tierCode?.code}
          <Typography
            component="span"
            sx={{
              color: tierCode?.status === FreeShippingCodesStatuses.used ? 'green' : 'red',
              ml: 0.5,
            }}
          >
            {`(${codeStatus})`}
          </Typography>
        </Grid>
      );
    }
    return null;
  }, [copied, tier, userFreeShipping, currentUserShipping, LevelStore.currentTier, FreeShippingStore.activeCode]);

  return React.useMemo<{
    content: React.ReactNode,
    subContent?: React.ReactNode
  }[]>(() => compact([
    cashbackCash && {
      content: (
        <Trans
          i18nKey="tier.cashback"
          defaults="{{gems, gems}} for every {{value, currency}} you spend"
          values={{
            gems: cashbackPoints * 10 ** ((PurchaseStore.purchases?.digits ?? 1) - 1),
            value: 100 * 10 ** ((PurchaseStore.purchases?.digits ?? 1) - 1),
            percentage: formatNumber(cashbackPoints),
          }}
        />
      ),
    },
    AppStore.app.agent === 'vtex' && levelFreeShipping?.enabled && {
      content: (
        <>
          <Trans
            i18nKey="level.freeShipping"
            defaults="Free Shipping"
          />
          <Typography component="span">
            <CustomTooltip
              sx={{
                height: 10,
                width: 10,
              }}
              title={freeShippingDescription}
              placement="top-start"
            >
              <Info color="primary" />
            </CustomTooltip>
          </Typography>
        </>
      ),
      subContent: codes,
    },
    ...tier?.benefits?.map((benefit) => ({
      content: <DisplayStringTrans displayString={benefit} />,
    })) ?? [],
    showCashbackBenefit && {
      content: (
        <Trans
          i18nKey="coreloyalty.cashback.tiers.cashbackBenefit"
          defaults="{{cashbackPercentage}}% de cashback em cada compra"
          values={{
            cashbackPercentage: tierAppCashback?.percentage,
          }}
        />
      ),
    },
  ]), [
    cashbackCash,
    cashbackPoints,
    PurchaseStore.purchases,
    AppStore.app,
    levelFreeShipping,
    freeShippingDescription,
    codes,
    tier,
    showCashbackBenefit,
    tierAppCashback,
  ]);
};
